/**
 * Realtime audit findings panel.
 *
 * Consumes `Finding[]` as produced by `@farmer/audit-rules`' `runAudit()` +
 * `checkCompleteness()` (or the server's `audit_findings` on submit rejection) — shape:
 * { level, code, title, now, rec, fix, keys }. Per farmer-form-engine-frontend skill §7:
 * errors red on top, warns yellow below, click a finding to jump to & highlight its first
 * related field.
 *
 * Two classes of finding, and only one of them gates submission:
 *  - `REQ:` / `CONS:` — the questionnaire's own `required` / `constraint`. These block,
 *    and no reason can clear them; the only fix is to fill in / correct the field.
 *  - everything else (A/B agronomic rules) — cross-check material for the supervisor at
 *    approval time. Shown in full, recorded in full, never blocks. The reason box on them
 *    is an optional explanation the enumerator can leave, not a key to unlock submit.
 *
 * ADR-8 reminder baked into the UI copy here: this panel is realtime UX only. The server
 * re-runs both checks on submit and is the only source of truth — see
 * views/submission-form.js for how a 422 AUDIT_BLOCKED response replaces this panel's
 * contents with the server's.
 */
import { isCompletenessCode } from '@farmer/audit-rules';

function esc(s) {
  return String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

/**
 * @param {() => void} [args.beforeJump] - màn nhập đang bị che (bản xem lại) thì mở ra trước khi nhảy.
 * @param {Array<{code: string, reason: string}>} [args.reasons] - giải trình đã gõ ở lượt vẽ trước. Bảng
 *   được vẽ lại sau mỗi thay đổi và khi mở bản xem lại; không mang theo thì lời giải trình mất trong im
 *   lặng ngay trước khi nộp.
 */
export function renderFindingsPanel({ container, findings, formContainer, editable = true, beforeJump, reasons = [] }) {
  container.innerHTML = '';
  const errors = findings.filter((f) => f.level === 'error');
  const warns = findings.filter((f) => f.level === 'warn');

  // Duy nhất nhóm này chặn nộp phiếu. Server lọc lại y hệt bằng cùng hàm khi nộp.
  const blocking = errors.filter((f) => isCompletenessCode(f.code || ''));
  const advisory = findings.length - blocking.length;
  const summary = document.createElement('div');
  summary.className = 'findings-summary';
  summary.innerHTML = findings.length
    ? `<span class="ms">${blocking.length ? 'error' : 'warning'}</span>
       <span>${
         blocking.length
           ? `${blocking.length} câu chưa đạt điều kiện bảng hỏi`
           : 'Đã đủ điều kiện nộp phiếu'
       }${advisory ? ` · ${advisory} mục đối chiếu` : ''}</span>`
    : `<span class="ms">verified</span><span>Không phát hiện bất thường.</span>`;
  container.appendChild(summary);

  if (advisory > 0) {
    // Nói thẳng ra một lần, thay vì để điều tra viên đoán vì sao khối đỏ A14 không
    // chặn được nút Nộp còn khối đỏ REQ thì có.
    const note = document.createElement('p');
    note.className = 'findings-note';
    note.textContent =
      'Mục đối chiếu là số liệu bất thường cần người duyệt xem lại — không chặn nộp phiếu.';
    container.appendChild(note);
  }

  const reasonInputs = new Map(); // code -> input element

  function renderItem(f) {
    const item = document.createElement('div');
    item.className = `finding-item ${f.level}`;
    const keys = f.keys || f.relatedKeys || [];
    item.innerHTML = `
      <button type="button" class="finding-head">
        <span class="ms">${f.level === 'error' ? 'error' : 'warning'}</span>
        <span class="finding-body">
          ${
            // Mã luật (A4) người duyệt có dùng để trao đổi. `REQ:<khoá câu trả lời>` thì chỉ là khoá kỹ
            // thuật dài, tiêu đề ngay dưới đã nói đủ.
            f.code && f.code !== '—' && !isCompletenessCode(f.code) ? `<span class="finding-code">${esc(f.code)}</span>` : ''
          }
          <span class="finding-title">${esc(f.title)}</span>
          ${f.now ? `<span class="finding-row"><b>Hiện tại:</b> ${esc(f.now)}</span>` : ''}
          ${f.rec ? `<span class="finding-row"><b>Khuyến nghị:</b> ${esc(f.rec)}</span>` : ''}
          ${f.fix ? `<span class="finding-row"><b>Nên làm:</b> ${esc(f.fix)}</span>` : ''}
        </span>
      </button>`;
    if (keys.length && formContainer) {
      item.querySelector('.finding-head').addEventListener('click', () => {
        beforeJump?.();
        jumpToKey(formContainer, keys[0]);
      });
    }
    if (isCompletenessCode(f.code || '')) {
      item.classList.add('must-fix');
      const note = document.createElement('div');
      note.className = 'finding-mustfix';
      note.innerHTML = `<span class="ms">lock</span><span>Điều kiện bắt buộc của bảng hỏi — phải sửa mới nộp được phiếu.</span>`;
      item.appendChild(note);
    } else if (f.level === 'error' && editable) {
      // Ô này KHÔNG còn là chìa khoá mở nút Nộp (mục đối chiếu không chặn nữa) — nó
      // là lời giải trình đi kèm phiếu, để người duyệt biết số liệu lạ đã được xem.
      const overrideWrap = document.createElement('div');
      overrideWrap.className = 'finding-override';
      overrideWrap.innerHTML = `<label>Giải trình cho người duyệt (không bắt buộc)</label>
        <input type="text" class="override-reason" placeholder="Vd: Đã xác minh lại với nông hộ, số liệu đúng.">`;
      const input = overrideWrap.querySelector('input');
      input.value = reasons.find((r) => r.code === f.code)?.reason ?? '';
      reasonInputs.set(f.code, input);
      item.appendChild(overrideWrap);
    }
    return item;
  }

  // Việc phải làm lên trên cùng, rồi mới tới thứ để đọc: nhóm chặn → lỗi đối chiếu →
  // cảnh báo. Không dựa vào thứ tự mảng gọi vào, vì nó đến từ ba nguồn khác nhau
  // (audit realtime, 422 của server, `findings` đã lưu của phiếu đã khoá).
  blocking.forEach((f) => container.appendChild(renderItem(f)));
  errors.filter((f) => !isCompletenessCode(f.code || '')).forEach((f) => container.appendChild(renderItem(f)));
  warns.forEach((f) => container.appendChild(renderItem(f)));

  return {
    /**
     * What actually stops the submit button: the questionnaire's own conditions, and
     * nothing else. A/B findings are cross-check material for the approver — leaving them
     * out of here is the whole point of the change (see the header comment).
     */
    getBlocking() {
      return blocking;
    },
    /** Explanations the enumerator typed against A/B findings -> submit payload. */
    getOverrides() {
      return errors
        .map((f) => ({ code: f.code, reason: (reasonInputs.get(f.code)?.value || '').trim() }))
        .filter((o) => o.reason);
    },
    hasErrors: () => errors.length > 0,
  };
}

export function jumpToKey(formContainer, key) {
  const target = formContainer.querySelector(`[data-key="${cssEscape(key)}"]`);
  if (!target) return;
  const section = target.closest('.form-section');
  if (section) section.classList.remove('collapsed');
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  target.classList.remove('audit-focus');
  // Force reflow so the animation can restart if the same field is clicked twice.
  void target.offsetWidth;
  target.classList.add('audit-focus');
  setTimeout(() => target.classList.remove('audit-focus'), 2000);
  const input = target.matches('input,select') ? target : target.querySelector('input,select');
  if (input && !input.disabled) input.focus({ preventScroll: true });
}

function cssEscape(s) {
  return window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}
